import {useState} from 'react';
import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription} from '@/components/ui/dialog';
import {Button} from '@/components/ui/button';
import {Label} from '@/components/ui/label';
import {Input} from '@/components/ui/input';
import {Textarea} from '@/components/ui/textarea';
import {useToast} from '@/hooks/use-toast';
import {Code2, Copy, CheckCheck} from 'lucide-react';
import {CodeBuilderConfig} from '../types';

interface RewardCodeBuilderModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const RewardCodeBuilderModal = ({isOpen, onClose}: RewardCodeBuilderModalProps) => {
    const {toast} = useToast();
    const [copied, setCopied] = useState(false);

    const [builderConfig, setBuilderConfig] = useState<CodeBuilderConfig>({
        text: 'Acessar Oferta',
        url: '',
        backgroundColor: '#4CAF50',
        textColor: '#FFFFFF',
        padding: '15px 30px',
        borderRadius: '5px',
        fontSize: '16px',
        fontWeight: 'bold',
        border: 'none',
        tagType: 'a',
        waitTopo: false
    });

    const generateCode = () => {
        const styles = [
            `display: inline-block`,
            `padding: ${builderConfig.padding}`,
            `background-color: ${builderConfig.backgroundColor}`,
            `color: ${builderConfig.textColor}`,
            `text-decoration: none`,
            `border-radius: ${builderConfig.borderRadius}`,
            `font-size: ${builderConfig.fontSize}`,
            `font-weight: ${builderConfig.fontWeight}`,
            `border: ${builderConfig.border}`,
            `cursor: pointer`
        ].join('; ');

        const waitTopoAttr = ` waittopo="${builderConfig.waitTopo}"`;

        if (builderConfig.tagType === 'a') {
            return `<a href="${builderConfig.url || '#'}" contentcode="Rewarded"${waitTopoAttr} style="${styles}">${builderConfig.text}</a>`;
        } else {
            return `<button contentcode="Rewarded"${waitTopoAttr} ${builderConfig.url ? `onclick="window.location.href='${builderConfig.url}'"` : ''} style="${styles}">${builderConfig.text}</button>`;
        }
    };

    const copyToClipboard = () => {
        const code = generateCode();
        navigator.clipboard.writeText(code);
        setCopied(true);
        toast({
            title: 'Código copiado!',
            description: 'O código foi copiado para a área de transferência.',
        });
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Code2 className="h-5 w-5"/>
                        Builder de Código Offerwall
                    </DialogTitle>
                    <DialogDescription>
                        Configure e gere o código HTML para integrar o reward diretamente no seu site
                    </DialogDescription>
                </DialogHeader>

                <div className="grid md:grid-cols-2 gap-6 mt-4">
                    {/* Configurações */}
                    <div className="space-y-4 max-h-[calc(90vh-200px)] overflow-y-auto pr-2">
                        <div className="space-y-2">
                            <Label>Tipo de Elemento</Label>
                            <div className="flex gap-2">
                                <Button
                                    type="button"
                                    variant={builderConfig.tagType === 'a' ? 'default' : 'outline'}
                                    onClick={() => setBuilderConfig({...builderConfig, tagType: 'a'})}
                                    className="flex-1"
                                >
                                    Link (&lt;a&gt;)
                                </Button>
                                <Button
                                    type="button"
                                    variant={builderConfig.tagType === 'button' ? 'default' : 'outline'}
                                    onClick={() => setBuilderConfig({...builderConfig, tagType: 'button'})}
                                    className="flex-1"
                                >
                                    Botão (&lt;button&gt;)
                                </Button>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="builder_text">Texto do Botão</Label>
                            <Input
                                id="builder_text"
                                value={builderConfig.text}
                                onChange={(e) => setBuilderConfig({...builderConfig, text: e.target.value})}
                                placeholder="Ex: Acessar Oferta"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="builder_url">URL de Redirecionamento</Label>
                            <Input
                                id="builder_url"
                                type="url"
                                value={builderConfig.url}
                                onChange={(e) => setBuilderConfig({...builderConfig, url: e.target.value})}
                                placeholder="https://exemplo.com/oferta"
                            />
                            <p className="text-xs text-gray-500">
                                {builderConfig.tagType === 'a' ? 'URL do link' : 'URL para redirecionar ao clicar'}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={builderConfig.waitTopo}
                                    onChange={(e) => setBuilderConfig({...builderConfig, waitTopo: e.target.checked})}
                                    className="h-4 w-4 rounded border-gray-300"
                                />
                                Aguardar compra do offerwall antes de imprimir o topo
                            </Label>
                            <p className="text-xs text-gray-500">
                                Se ativado, o topo só será impresso após a compra do offerwall ser concluída
                            </p>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="builder_bg">Cor de Fundo</Label>
                                <div className="flex gap-2">
                                    <Input
                                        id="builder_bg"
                                        type="color"
                                        value={builderConfig.backgroundColor}
                                        onChange={(e) => setBuilderConfig({
                                            ...builderConfig,
                                            backgroundColor: e.target.value
                                        })}
                                        className="h-10 w-16"
                                    />
                                    <Input
                                        type="text"
                                        value={builderConfig.backgroundColor}
                                        onChange={(e) => setBuilderConfig({
                                            ...builderConfig,
                                            backgroundColor: e.target.value
                                        })}
                                        className="h-10 flex-1 font-mono text-xs"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="builder_text_color">Cor do Texto</Label>
                                <div className="flex gap-2">
                                    <Input
                                        id="builder_text_color"
                                        type="color"
                                        value={builderConfig.textColor}
                                        onChange={(e) => setBuilderConfig({
                                            ...builderConfig,
                                            textColor: e.target.value
                                        })}
                                        className="h-10 w-16"
                                    />
                                    <Input
                                        type="text"
                                        value={builderConfig.textColor}
                                        onChange={(e) => setBuilderConfig({
                                            ...builderConfig,
                                            textColor: e.target.value
                                        })}
                                        className="h-10 flex-1 font-mono text-xs"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="builder_padding">Padding (Espaçamento Interno)</Label>
                            <Input
                                id="builder_padding"
                                value={builderConfig.padding}
                                onChange={(e) => setBuilderConfig({...builderConfig, padding: e.target.value})}
                                placeholder="15px 30px"
                            />
                            <p className="text-xs text-gray-500">Ex: 15px 30px (vertical horizontal)</p>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="builder_border_radius">Border Radius (Arredondamento)</Label>
                            <Input
                                id="builder_border_radius"
                                value={builderConfig.borderRadius}
                                onChange={(e) => setBuilderConfig({...builderConfig, borderRadius: e.target.value})}
                                placeholder="5px"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="builder_font_size">Tamanho da Fonte</Label>
                                <Input
                                    id="builder_font_size"
                                    value={builderConfig.fontSize}
                                    onChange={(e) => setBuilderConfig({...builderConfig, fontSize: e.target.value})}
                                    placeholder="16px"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="builder_font_weight">Peso da Fonte</Label>
                                <select
                                    id="builder_font_weight"
                                    value={builderConfig.fontWeight}
                                    onChange={(e) => setBuilderConfig({
                                        ...builderConfig,
                                        fontWeight: e.target.value
                                    })}
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                >
                                    <option value="normal">Normal</option>
                                    <option value="bold">Bold</option>
                                    <option value="600">Semi-Bold (600)</option>
                                    <option value="700">Bold (700)</option>
                                    <option value="800">Extra-Bold (800)</option>
                                </select>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="builder_border">Borda</Label>
                            <Input
                                id="builder_border"
                                value={builderConfig.border}
                                onChange={(e) => setBuilderConfig({...builderConfig, border: e.target.value})}
                                placeholder="none ou 2px solid #000"
                            />
                            <p className="text-xs text-gray-500">Ex: 2px solid #000000 ou none</p>
                        </div>
                    </div>

                    {/* Preview e Código */}
                    <div className="space-y-4">
                        <div>
                            <Label className="mb-2 block">Preview</Label>
                            <div className="bg-gray-50 rounded-lg p-6 flex items-center justify-center min-h-[200px]">
                                <div
                                    dangerouslySetInnerHTML={{__html: generateCode()}}
                                    className="pointer-events-none"
                                />
                            </div>
                        </div>

                        <div>
                            <Label className="mb-2 block">Código Gerado</Label>
                            <div className="relative">
                                <Textarea
                                    value={generateCode()}
                                    readOnly
                                    rows={8}
                                    className="font-mono text-xs bg-gray-900 text-green-400 pr-12"
                                />
                                <Button
                                    type="button"
                                    size="sm"
                                    onClick={copyToClipboard}
                                    className="absolute top-2 right-2"
                                    variant="secondary"
                                >
                                    {copied ? (
                                        <>
                                            <CheckCheck className="h-4 w-4 mr-2"/>
                                            Copiado!
                                        </>
                                    ) : (
                                        <>
                                            <Copy className="h-4 w-4 mr-2"/>
                                            Copiar
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <h4 className="font-semibold text-blue-900 mb-2 text-sm">📋 Como usar</h4>
                            <ul className="text-xs text-blue-800 space-y-1">
                                <li>• Cole o código HTML no seu site</li>
                                <li>• O atributo <code className="bg-blue-100 px-1 rounded">contentcode="Rewarded"</code> é obrigatório</li>
                                <li>• Ao clicar, o reward será exibido automaticamente</li>
                                <li>• Se tiver URL, será redirecionado após fechar o reward</li>
                                <li>• Use <code className="bg-blue-100 px-1 rounded">waittopo="true"</code> para aguardar a compra antes de imprimir o topo</li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                    <Button variant="outline" onClick={onClose}>
                        Fechar
                    </Button>
                    <Button onClick={copyToClipboard} className="bg-blue-600 hover:bg-blue-700">
                        <Copy className="h-4 w-4 mr-2"/>
                        Copiar Código
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};